import { useState, useEffect, useRef } from 'react';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useDebounce } from '@/hooks/useDebounce';
import type { Tables } from '@/types/database.types';
import { KpiHistoryPanel } from './KpiHistoryPanel';
import type { KpiHistoryPoint } from './ProjectDashboard';

type KpiRow = Tables<'kpis'>;

export type KpiUpdates = Partial<{
  name: string;
  target: number | null;
  currentValue: number | null;
  unit: string | null;
}>;

function progressPercent(current: number | null, target: number | null): number | null {
  if (target === null || target === 0) return null;
  if (current === null) return 0;
  return Math.max(0, Math.min(100, (current / target) * 100));
}

export function KpiItem({
  kpi,
  history,
  onUpdate,
  onDelete,
  onAddHistoryPoint,
  onUpdateHistoryPoint,
  onDeleteHistoryPoint,
  readOnly = false,
}: {
  kpi: KpiRow;
  history: KpiHistoryPoint[];
  onUpdate: (id: string, updates: KpiUpdates) => void;
  onDelete: (id: string) => void;
  onAddHistoryPoint: (kpiId: string, value: number, recordedAt: string) => Promise<void>;
  onUpdateHistoryPoint: (kpiId: string, historyId: string, value: number, recordedAt: string) => Promise<void>;
  onDeleteHistoryPoint: (kpiId: string, historyId: string) => Promise<void>;
  readOnly?: boolean;
}) {
  // History panel collapses by default so the KPI list stays scannable;
  // expanded state survives autosave-driven rerenders.
  const [showHistory, setShowHistory] = useState(false);
  const [name, setName] = useState(kpi.name);
  const [target, setTarget] = useState<string>(kpi.target?.toString() ?? '');
  const [currentValue, setCurrentValue] = useState<string>(kpi.current_value?.toString() ?? '');
  const [unit, setUnit] = useState<string>(kpi.unit ?? '');

  const debouncedName = useDebounce(name, 800);
  const debouncedTarget = useDebounce(target, 800);
  const debouncedCurrent = useDebounce(currentValue, 800);
  const debouncedUnit = useDebounce(unit, 800);
  const initialRef = useRef(true);

  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }
    const updates: KpiUpdates = {};
    if (debouncedName !== kpi.name) updates.name = debouncedName;
    const targetNum = debouncedTarget === '' ? null : Number(debouncedTarget);
    const currentNum = debouncedCurrent === '' ? null : Number(debouncedCurrent);
    if (targetNum !== kpi.target && !(Number.isNaN(targetNum as number))) updates.target = targetNum;
    if (currentNum !== kpi.current_value && !(Number.isNaN(currentNum as number))) updates.currentValue = currentNum;
    if ((debouncedUnit || null) !== (kpi.unit || null)) updates.unit = debouncedUnit || null;
    if (Object.keys(updates).length > 0) {
      onUpdate(kpi.id, updates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName, debouncedTarget, debouncedCurrent, debouncedUnit]);

  const pct = progressPercent(
    currentValue === '' ? null : Number(currentValue),
    target === '' ? null : Number(target),
  );

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg group">
      <div className="flex-1 space-y-2">
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="font-medium border-0 bg-transparent px-0 h-auto focus-visible:ring-0"
          placeholder="KPI name (e.g., Active users)"
          readOnly={readOnly}
        />
        <div className="flex gap-2 items-center flex-wrap">
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Current</label>
            <Input
              type="number"
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              placeholder="0"
              className="w-24 text-sm h-8"
              readOnly={readOnly}
            />
          </div>
          <span className="text-gray-400 text-sm">/</span>
          <div className="flex items-center gap-1">
            <label className="text-xs text-gray-500">Target</label>
            <Input
              type="number"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="—"
              className="w-24 text-sm h-8"
              readOnly={readOnly}
            />
          </div>
          <Input
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            placeholder="Unit (%, $, users…)"
            className="w-40 text-sm h-8"
            readOnly={readOnly}
          />
        </div>
        {pct !== null && (
          <div className="flex items-center gap-2 pt-1">
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-ingesta-blue-500 transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 tabular-nums w-10 text-right">
              {Math.round(pct)}%
            </span>
          </div>
        )}

        {/* History toggle — always visible so users discover the panel
            even when there are no recorded measurements yet. */}
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="mt-1 flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
        >
          {showHistory ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {showHistory ? 'Hide' : 'Show'} history ({history.length})
        </button>

        {showHistory && (
          <KpiHistoryPanel
            history={history}
            unit={kpi.unit}
            readOnly={readOnly}
            onAddPoint={(value, recordedAt) => onAddHistoryPoint(kpi.id, value, recordedAt)}
            onUpdatePoint={(id, value, recordedAt) => onUpdateHistoryPoint(kpi.id, id, value, recordedAt)}
            onDeletePoint={(id) => onDeleteHistoryPoint(kpi.id, id)}
          />
        )}
      </div>
      {!readOnly && (
        <button
          onClick={() => onDelete(kpi.id)}
          className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-1"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
